import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash2, CheckCircle2, XCircle, Wifi, Pencil, Power, Star,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Select } from "@/components/ui/Select.tsx";
import { Textarea } from "@/components/ui/Textarea.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Modal } from "@/components/ui/Modal.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { adminApi, type PaymentGateway, type GatewayProvider, type ProviderField } from "@/api/admin.ts";
import type { ApiError } from "@/api/client.ts";

export function AdminGatewaysPage() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "gateways"],
    queryFn: () => adminApi.listGateways(),
  });

  const deleteGw = useMutation({
    mutationFn: (id: string) => adminApi.deleteGateway(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "gateways"] }),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminApi.updateGateway(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "gateways"] }),
  });

  const testGw = useMutation({ mutationFn: (id: string) => adminApi.testGateway(id) });

  const gateways = data?.gateways ?? [];
  const providers = data?.availableProviders ?? [];

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-fg">Payment Gateways</h1>
          <p className="mt-1 text-fgMuted">Configure payment providers for subscription billing</p>
        </div>
        <Button onClick={() => setShowAdd(true)}><Plus className="h-4 w-4" /> Add Gateway</Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : gateways.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-fgMuted mb-4">No payment gateways configured yet.</p>
            <Button onClick={() => setShowAdd(true)}><Plus className="h-4 w-4" /> Add your first gateway</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {gateways.map((gw) => (
            <Card key={gw.id} className={gw.isActive ? "" : "opacity-60"}>
              <CardContent className="flex items-center gap-4 py-5">
                {/* Provider icon placeholder */}
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-surface2 text-lg font-bold text-fgMuted uppercase">
                  {gw.provider.slice(0, 2)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-fg">{gw.name}</p>
                    {gw.isDefault && <Badge variant="warning"><Star className="mr-1 h-3 w-3" />Default</Badge>}
                    <Badge variant={gw.isActive ? "success" : "muted"}>
                      {gw.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-sm text-fgMuted capitalize">
                    {gw.provider} · {(gw.supportedCurrencies as string[]).join(", ")}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost" size="sm"
                    loading={testGw.isPending && testGw.variables === gw.id}
                    onClick={() => testGw.mutate(gw.id)}
                  >
                    <Wifi className="h-3.5 w-3.5" /> Test
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleActive.mutate({ id: gw.id, isActive: !gw.isActive })}>
                    <Power className="h-3.5 w-3.5" /> {gw.isActive ? "Disable" : "Enable"}
                  </Button>
                  <Button variant="ghost" size="sm" aria-label="Edit this gateway" onClick={() => setEditingId(gw.id)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { if (confirm(`Delete gateway "${gw.name}"?`)) deleteGw.mutate(gw.id); }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-danger" />
                  </Button>
                </div>
              </CardContent>

              {/* Show test result inline */}
              {testGw.isSuccess && testGw.variables === gw.id && (
                <div className={`border-t border-border px-6 py-3 text-sm flex items-center gap-2 ${testGw.data.success ? "text-success" : "text-danger"}`}>
                  {testGw.data.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {testGw.data.message}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <GatewayFormModal
          providers={providers}
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); queryClient.invalidateQueries({ queryKey: ["admin", "gateways"] }); }}
        />
      )}

      {/* Edit modal */}
      {editingId && (
        <GatewayFormModal
          providers={providers}
          existing={gateways.find((g) => g.id === editingId)}
          onClose={() => setEditingId(null)}
          onSuccess={() => { setEditingId(null); queryClient.invalidateQueries({ queryKey: ["admin", "gateways"] }); }}
        />
      )}
    </div>
  );
}

function GatewayFormModal({
  providers,
  existing,
  onClose,
  onSuccess,
}: {
  providers: GatewayProvider[];
  existing?: PaymentGateway;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name ?? "");
  const [provider, setProvider] = useState(existing?.provider ?? "");
  const [isActive, setIsActive] = useState(existing?.isActive ?? false);
  const [isDefault, setIsDefault] = useState(existing?.isDefault ?? false);
  const [currencies, setCurrencies] = useState((existing?.supportedCurrencies as string[] ?? ["USD"]).join(", "));
  const [config, setConfig] = useState<Record<string, string>>(
    (existing?.config as Record<string, string>) ?? {},
  );

  const selectedProvider = providers.find((p) => p.id === provider);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        provider,
        isActive,
        isDefault,
        config,
        supportedCurrencies: currencies.split(",").map((c) => c.trim()).filter(Boolean),
      };
      return isEdit ? adminApi.updateGateway(existing!.id, body) : adminApi.createGateway(body);
    },
    onSuccess,
  });

  const error = save.error as ApiError | null;

  return (
    <Modal
      open onClose={onClose}
      title={isEdit ? `Edit Gateway — ${existing!.name}` : "Add Payment Gateway"}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button loading={save.isPending} disabled={!name || !provider} onClick={() => save.mutate()}>
            {isEdit ? "Save Changes" : "Create Gateway"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 max-h-[65vh] overflow-y-auto">
        <Input label="Gateway Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Stripe US, PayPal EU" required />

        <Select
          label="Provider"
          value={provider}
          onChange={(e) => { setProvider(e.target.value); setConfig({}); }}
          placeholder="Select provider…"
          options={providers.map((p) => ({ value: p.id, label: p.name }))}
          disabled={isEdit}
        />

        <Input label="Supported Currencies" value={currencies} onChange={(e) => setCurrencies(e.target.value)} placeholder="USD, EUR, GBP" hint="Comma-separated ISO currency codes" />

        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded border-border" />
            Active
          </label>
          <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
            <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="rounded border-border" />
            Default gateway
          </label>
        </div>

        {/* Dynamic provider config fields */}
        {selectedProvider && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base capitalize">{selectedProvider.name} Configuration</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {selectedProvider.configFields.map((field) => (
                <ConfigField
                  key={field.key}
                  field={field}
                  value={config[field.key] ?? ""}
                  onChange={(val) => setConfig({ ...config, [field.key]: val })}
                />
              ))}
            </CardContent>
          </Card>
        )}

        {error && <p className="text-sm text-danger">{error.message}</p>}
      </div>
    </Modal>
  );
}

function ConfigField({ field, value, onChange }: { field: ProviderField; value: string; onChange: (v: string) => void }) {
  if (field.type.startsWith("select:")) {
    const options = field.type.replace("select:", "").split(",");
    return (
      <Select
        label={field.label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Select ${field.label.toLowerCase()}…`}
        options={options.map((o) => ({ value: o, label: o.charAt(0).toUpperCase() + o.slice(1) }))}
        required={field.required}
      />
    );
  }
  if (field.type === "textarea") {
    return (
      <Textarea
        label={field.label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        required={field.required}
      />
    );
  }
  return (
    <Input
      label={field.label}
      type={field.type === "password" ? "password" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.secret ? "••••••••" : `Enter ${field.label.toLowerCase()}`}
      required={field.required}
    />
  );
}
